import React, { useContext, useEffect, useRef, useState } from 'react'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Spinner from 'react-bootstrap/Spinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowUp,
  faBell,
  faBuilding,
  faChartBar,
  faCheck,
  faClipboardCheck,
  faComments,
  faCreditCard,
  faDatabase,
  faExclamationTriangle,
  faFileUpload,
  faFlag,
  faRedo,
  faSearchDollar,
  faStop,
  faTachometerAlt,
  faUserTie
} from '@fortawesome/free-solid-svg-icons'
import { ActionButton } from 'shared/buttons'
import MarkdownMessage from 'shared/markdown-message'
import UserContext from 'context/user-context'
import AskChart from './ask-chart'
import CreatedCard, { createdHref } from './created-card'
import EmailCard from './email-card'

// What a new conversation offers to start with: questions with a sensible default come first and
// answer in one turn; the builders after them open a short interview for the specifics. A starter with
// anySection is offered only to a user holding one of those sections, as the sidebar gates its page.
export const STARTERS = [
  {
    title: 'Explore spending',
    description: 'Top vendors, departments and trends',
    icon: faSearchDollar,
    message: 'What did we spend by vendor this fiscal year?'
  },
  {
    title: 'Review flagged transactions',
    description: 'What your analytics flag, and where',
    icon: faFlag,
    message: 'Which analytics flag the most transactions, and which departments have the highest rate?'
  },
  {
    title: 'Review monitoring',
    description: "What's waiting for review, and where",
    icon: faTachometerAlt,
    message: "What's waiting for review in Monitoring?"
  },
  {
    title: 'Check policy compliance',
    description: 'Where spending breaks your policy rules',
    icon: faClipboardCheck,
    message: 'Which of our policy rules are being broken, and by how much?'
  },
  {
    title: 'Look into a vendor',
    description: 'Every record for one vendor',
    icon: faBuilding,
    message: 'Help me look into a vendor.'
  },
  {
    title: 'Look into an employee',
    description: 'Transactions they entered, approved or made',
    icon: faUserTie,
    message: 'Help me look into an employee.'
  },
  {
    title: 'Review P-Card activity',
    description: 'Merchant categories, approvers and cardholders',
    icon: faCreditCard,
    message: 'Give me an overview of P-Card activity this fiscal year.'
  },
  {
    title: 'Upload receipts or invoices',
    description: 'Match them to transactions, and check on uploads',
    icon: faFileUpload,
    message: 'How do I upload receipts or invoices, and which of my uploads still need attention?',
    anySection: ['AP', 'PC']
  },
  {
    title: 'Chart a topic',
    description: 'A few charts that answer one question',
    icon: faChartBar,
    message: 'Help me chart a topic.'
  },
  {
    title: 'Build a View',
    description: 'Filtered data you can open and keep',
    icon: faDatabase,
    message: 'Help me build a View.'
  },
  {
    title: 'Create an analytic',
    description: 'Flag transactions like these on every run',
    icon: faBell,
    message: 'Help me create an analytic that flags transactions.'
  }
]

export const startersFor = (user) => {
  const held = user?.section_abbreviations ?? []
  return STARTERS.filter(({ anySection }) => !anySection || anySection.some((abbreviation) => held.includes(abbreviation)))
}

// A chart the assistant redrew appears once, where it was last drawn; one it removed, nowhere.
const lastChartEventIds = (events) => {
  const latest = new Map()
  events.forEach((event) => {
    const chartId = event.payload?.report_chart_id
    if (event.type === 'chart') latest.set(chartId, event.id)
    if (event.type === 'chart_removed') latest.delete(chartId)
  })
  return new Set(latest.values())
}

// Events that mark a turn's progress rather than say anything.
const BOOKKEEPING_EVENTS = ['started', 'done']

const lastIdOf = (events, type) => [...events].reverse().find((event) => event.type === type)?.id

// The question a failed turn was answering: the last thing the user said before the error.
const questionBefore = (events, errorId) => {
  const index = events.findIndex((event) => event.id === errorId)
  return events.slice(0, index).reverse().find((event) => event.type === 'user_text')?.payload?.text
}

const Bubble = ({ children, mine = false }) => (
  <div className={`mb-3 ${mine ? 'text-end' : ''}`}>
    <div
      className={`d-inline-block rounded-3 px-3 py-2 text-start ${mine ? 'bg-primary text-white' : 'bg-light text-primary'}`}
      style={{ maxWidth: '85%' }}
    >
      {children}
    </div>
  </div>
)

const Composer = ({
  input, setInput, onKeyDown, placeholder, rows, isRunning, isStopping, isSending, onSend, onStop, textareaRef
}) => (
  <div className="d-flex gap-2 align-items-end">
    <Form.Control
      ref={textareaRef}
      as="textarea"
      rows={rows}
      value={input}
      onChange={(event) => setInput(event.target.value)}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      aria-label="Ask a question"
      data-testid="ask-input"
      maxLength={4000}
    />
    {isRunning ? (
      <ActionButton
        variant="light"
        className="border text-primary text-nowrap"
        icon={faStop}
        label="Stop"
        loading={isStopping}
        loadingLabel="Stopping…"
        disabled={isSending}
        onClick={onStop}
        data-testid="ask-stop"
      />
    ) : (
      <ActionButton
        icon={faArrowUp}
        label="Send"
        className="text-nowrap"
        disabled={!input.trim()}
        onClick={() => onSend()}
        data-testid="ask-send"
      />
    )}
  </div>
)

const StarterCard = ({ starter, onPick, disabled }) => (
  <Button
    variant="light"
    className="border text-primary text-start w-100 h-100 p-3"
    onClick={() => onPick(starter.message)}
    disabled={disabled}
    data-testid="ask-starter"
  >
    <div className="d-flex align-items-center gap-2 fw-semibold mb-1">
      <FontAwesomeIcon icon={starter.icon} fixedWidth />
      {starter.title}
    </div>
    <div className="small text-muted">{starter.description}</div>
  </Button>
)

const AskThread = ({
  conversation,
  isRunning,
  isStopping,
  isSending,
  pendingUserText,
  focusKey,
  onSend,
  onStop
}) => {
  const user = useContext(UserContext)
  const [input, setInput] = useState('')
  const bottomRef = useRef(null)
  const textareaRef = useRef(null)
  const events = conversation?.events ?? []
  const chartsById = Object.fromEntries((conversation?.charts ?? []).map((chart) => [chart.id, chart]))
  const visibleChartEvents = lastChartEventIds(events)
  const awaitingInput = conversation?.status === 'awaiting_input'
  // Only the newest question is still open; chips on an earlier one would answer this one instead.
  const openQuestionId = awaitingInput ? lastIdOf(events, 'question') : null
  // Only the newest error can be retried, and only with the question that failed.
  const latestErrorId = lastIdOf(events, 'error')
  // Next steps belong to the answer they follow: shown only while it is the latest thing said.
  const lastSaid = [...events].reverse().find((event) => !BOOKKEEPING_EVENTS.includes(event.type))
  const openSuggestionsId = lastSaid?.type === 'suggestions' ? lastSaid.id : null
  const retryText = latestErrorId ? questionBefore(events, latestErrorId) : null

  useEffect(() => {
    bottomRef.current?.scrollIntoView?.({ block: 'end' })
  }, [events.length, pendingUserText, isRunning])

  useEffect(() => {
    if (focusKey) textareaRef.current?.focus()
  }, [focusKey])

  // Typed text is cleared on send and put back if the send fails, so a rejected question isn't lost.
  const send = (text) => {
    const message = (text ?? input).trim()
    if (!message || isRunning) return
    const typed = text === undefined
    if (typed) setInput('')
    onSend(message, { onFailure: () => typed && setInput((current) => current || message) })
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      send()
    }
  }

  const renderEvent = ({ id, type, payload }) => {
    switch (type) {
      case 'user_text':
        return <Bubble key={id} mine>{payload.text}</Bubble>
      case 'assistant_text':
        return (
          <Bubble key={id}>
            <MarkdownMessage text={payload.text} />
          </Bubble>
        )
      case 'action':
        if (createdHref(payload.link)) {
          return (
            <div key={id} data-testid="ask-action">
              <CreatedCard link={payload.link} />
            </div>
          )
        }
        return (
          <div key={id} className="mb-2 small text-muted d-flex align-items-center gap-2" data-testid="ask-action">
            <FontAwesomeIcon icon={faCheck} className="text-success" />
            {payload.summary}
          </div>
        )
      case 'chart': {
        const chart = chartsById[payload.report_chart_id]
        if (!chart || !visibleChartEvents.has(id)) return null
        return <AskChart key={id} chart={chart} reportId={conversation.report_id} />
      }
      case 'email':
        return <EmailCard key={id} email={payload} />
      case 'question':
        return (
          <div key={id} className="mb-3">
            <Bubble>
              <MarkdownMessage text={payload.text} />
            </Bubble>
            {(payload.options?.length ?? 0) > 0 && (
              <div className="d-flex flex-wrap gap-2">
                {payload.options.map((option) => (
                  <Button
                    key={option.value}
                    size="sm"
                    variant="light"
                    className="border text-primary"
                    onClick={() => send(option.value)}
                    disabled={id !== openQuestionId || isRunning}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        )
      case 'suggestions':
        if (id !== openSuggestionsId || isRunning) return null
        return (
          <div key={id} className="mb-3 d-flex flex-wrap gap-2" data-testid="ask-suggestions">
            {(payload.steps ?? []).map((step) => (
              <Button key={step} size="sm" variant="light" className="border text-primary" onClick={() => send(step)}>
                {step}
              </Button>
            ))}
          </div>
        )
      case 'error':
        return (
          <div key={id} className="mb-3 d-flex align-items-start gap-2" data-testid="ask-error">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger mt-2" />
            <div className="rounded-3 px-3 py-2 bg-danger bg-opacity-10 text-danger">
              {payload.text}
              {id === latestErrorId && payload.retryable !== false && retryText && !isRunning && (
                <ActionButton
                  variant="link"
                  icon={faRedo}
                  label="Retry"
                  className="d-block p-0 mt-1 text-danger"
                  onClick={() => send(retryText)}
                  data-testid="ask-retry"
                />
              )}
            </div>
          </div>
        )
      default:
        return null
    }
  }

  const isEmpty = events.length === 0 && !pendingUserText
  const composerProps = {
    input,
    setInput,
    textareaRef,
    onKeyDown: handleKeyDown,
    isRunning,
    isStopping,
    isSending,
    onSend: send,
    onStop
  }

  if (isEmpty) {
    return (
      <div className="h-100 overflow-auto d-flex" data-testid="ask-empty">
        <div className="m-auto w-100 py-4" style={{ maxWidth: 760 }}>
          <h2 className="text-primary text-center mb-4">
            <FontAwesomeIcon icon={faComments} className="me-2" />
            What can I help you with?
          </h2>
          <Composer {...composerProps} rows={3} placeholder="Ask about your data…" />
          <Row xs={1} sm={2} lg={3} className="g-2 mt-3">
            {startersFor(user).map((starter) => (
              <Col key={starter.title}>
                <StarterCard starter={starter} onPick={send} disabled={isRunning} />
              </Col>
            ))}
          </Row>
        </div>
      </div>
    )
  }

  return (
    <div className="d-flex flex-column h-100">
      <div className="flex-grow-1 overflow-auto px-1" data-testid="ask-thread">
        {events.map(renderEvent)}
        {pendingUserText && <Bubble mine>{pendingUserText}</Bubble>}
        {isRunning && (
          <div className="mb-3 small text-muted d-flex align-items-center gap-2" data-testid="ask-working">
            <Spinner animation="border" size="sm" />
            {isStopping ? 'Stopping…' : 'Working on it…'}
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="border-top pt-3">
        <Composer
          {...composerProps}
          rows={2}
          placeholder={awaitingInput ? 'Answer the assistant…' : 'Ask about your data…'}
        />
      </div>
    </div>
  )
}

export default AskThread
